import React, { useEffect, useRef, useState } from 'react';

import { Autocomplete, AutocompleteItem, AutocompleteProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteNotificationMessageTemplates from 'hooks/notificationMessageTemplate/useAutoCompleteNotificationMessageTemplates';
import { NotificationMessageTemplateAutoCompleteValue } from 'pages/private/owner/FlowBuilder/types';

import SelectorButton from './SelectorButton';

interface INotificationMessageTemplateAutoCompleteProps
  extends Omit<
    AutocompleteProps,
    'data' | 'value' | 'onChange' | 'filter' | 'onItemSubmit' | 'rightSection'
  > {
  value: NotificationMessageTemplateAutoCompleteValue;
  onChange: (item: NotificationMessageTemplateAutoCompleteValue | null) => void;
  notificationType: 'Email' | 'SMS' | null;
}

const NotificationMessageTemplateAutoComplete = ({
  value,
  onChange,
  notificationType,
  ...autocompleteProps
}: INotificationMessageTemplateAutoCompleteProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<AutocompleteItem[]>([]);
  const [search, setSearch] = useState(value.label);
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const { data, isSuccess, isLoading } = useAutoCompleteNotificationMessageTemplates({
    search: debouncedSearch,
    notificationType: notificationType,
  });

  const handleSearchChange = (query: string): void => {
    setSearch(query);
    onChange(null);
  };
  const handleItemSubmit = (item: NotificationMessageTemplateAutoCompleteValue): void => {
    setSearch('');
    onChange(item);
  };
  const handleClearSearch = (): void => {
    setSearch('');
  };

  const RightSection = (
    <SelectorButton
      isLoading={isLoading}
      hasText={!!(value.label || search)}
      handleClick={() => handleSearchChange('')}
      handleFocus={() => inputRef?.current?.focus()}
    />
  );

  useEffect(() => {
    if (isSuccess && data) {
      setOptions(
        data.data.map(notificationMessageTemplate => ({
          ...notificationMessageTemplate,
          value: notificationMessageTemplate.notificationMessageTemplateId,
          label: notificationMessageTemplate.description,
        })),
      );
    }
  }, [data, isSuccess]);

  return (
    <Autocomplete
      ref={inputRef}
      rightSection={RightSection}
      value={value.label || search}
      onChange={handleSearchChange}
      onItemSubmit={handleItemSubmit}
      onDropdownClose={handleClearSearch}
      filter={() => true}
      data={options}
      {...autocompleteProps}
    />
  );
};

export default NotificationMessageTemplateAutoComplete;
